/**
 * Invalidates "live" React Query caches whenever the app comes back to the
 * foreground. Critical for native (Capacitor) where window-focus doesn't fire,
 * so reopening the app from background was showing stale Home/Discover data
 * until staleTime expired and a manual interaction triggered a refetch.
 *
 * Listens to both:
 *  - `document.visibilitychange` (web + Capacitor WebView)
 *  - `@capacitor/app` `appStateChange` (reliable on iOS/Android)
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { keys } from "@/data/keys";

const LIVE_SCOPES = [
  keys.checkins.all,
  keys.burgers.all,
  keys.discover.all,
  keys.leaderboards.all,
  keys.notifications.all,
] as const;

export function useAppForegroundRefresh() {
  const qc = useQueryClient();

  useEffect(() => {
    let lastRun = 0;
    const refresh = () => {
      // Throttle: don't spam invalidations if multiple events fire together.
      const now = Date.now();
      if (now - lastRun < 1000) return;
      lastRun = now;
      for (const scope of LIVE_SCOPES) {
        qc.invalidateQueries({ queryKey: scope });
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);

    let removeNativeListener: (() => void) | null = null;
    if (Capacitor.isNativePlatform()) {
      const handle = App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) refresh();
      });
      removeNativeListener = () => {
        handle.then((h) => h.remove()).catch(() => {});
      };
    }

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      removeNativeListener?.();
    };
  }, [qc]);
}
